"use client";

import SalifyComponent, {
  ISalifyComponentContext,
} from "src/lib/components/salify-component/default";
import FramedLayoutRenderer from "src/lib/site-builder/v2/framed-layout-renderer";
import { isFramedSiteDocument } from "src/lib/site-builder/v2/frame-layout.utils";
import DocumentThemeProvider from "./document-theme-provider";
import getter from "lodash.get";
import React, { useEffect, useMemo } from "react";
import componentConfig from "../salify-component/component.json";

type ObjectLiteral = { [key: string]: any };
const EMPTY_SYMBOL = "fallback:empty";

interface ISalifyContext {
  document: any;
  fallbacks: any;
  setFallbacks(callback: (state: ObjectLiteral) => ObjectLiteral): any;
  data: any;
  parent?: ISalifyContext;
  simulator?: boolean;
}

interface ISalifyContextItem<T = any> {
  value: T;
  index: number;
}

interface IItemContext {
  value: any;
}

const SalifyFallbackContext = React.createContext<ISalifyContext>(null as any);
const SalifyFallbackRepeaterItemContext = React.createContext<ISalifyContextItem>(null as any);

function SalifyContextProvider(
  props: Readonly<{
    document: any;
    children: React.ReactNode;
    simulator?: boolean;
    onChangeFallbacks?: (value: ObjectLiteral) => void;
  }>,
) {
  const [fallbacks, setFallbacks] = React.useState<ObjectLiteral>({});
  const previousCtx = React.useContext(SalifyFallbackContext);

  React.useEffect(() => {
    props.onChangeFallbacks?.(fallbacks);
  }, [fallbacks, props]);

  const value: ISalifyContext = useMemo(
    () => ({
      document: props.document,
      fallbacks: fallbacks,
      setFallbacks: setFallbacks,
      data: props.document,
      parent: previousCtx,
      simulator: props.simulator,
    }),
    [fallbacks, previousCtx, props.document, props.simulator],
  );

  return (
    <SalifyFallbackContext.Provider value={value}>{props.children}</SalifyFallbackContext.Provider>
  );
}

type SalifyFallbackCompProps = { path: string; default?: any; prefixPath?: string } & {
  [key: string]: any;
};
type SalifyFallbackCompOptions = SalifyFallbackCompProps & { path: string } & {
  [key: string]: any;
};
type SalifyFallbackProps = {
  document: any;
  default?: any;
  simulator?: boolean;
  onChangeFallbacks?: (value: ObjectLiteral) => void;
} & {
  [key: string]: any;
};

export const SalifyFallback = (props: SalifyFallbackProps) => {
  return (
    <SalifyContextProvider
      document={props.document}
      simulator={props.simulator}
      onChangeFallbacks={props.onChangeFallbacks}
    >
      <DocumentThemeProvider theme={props.document?.theme}>
        <div style={{ display: "flex", minHeight: "100vh", flexDirection: "column" }}>
          {isFramedSiteDocument(props.document) ? (
            <FramedLayoutRenderer document={props.document} />
          ) : (
            <SalifyComponent components={props.document.components} {...props} />
          )}
        </div>
      </DocumentThemeProvider>
    </SalifyContextProvider>
  );
};
SalifyFallback.displayName = "SalifyFallback";

export const SalifyFallbackRepeater = (
  props: {
    render(opts: { item: any; index: number }): React.ReactNode;
  } & (({ value: any[] } & Omit<SalifyFallbackCompProps, "path">) | SalifyFallbackCompProps),
) => {
  const context = React.useContext(SalifyFallbackContext);
  const list: any[] = useFallback<any[]>({ ...props, $type: "repeater", path: props.path ?? "" });
  const result = React.useMemo<any[]>(() => props.value || list, [props.value, list]);

  return result?.length ? (
    <SalifyContextProvider document={context.document}>
      {result.map((item, index) => {
        return (
          <SalifyFallbackRepeaterItemContext.Provider
            key={`${props.path}_${index}`}
            value={{ value: result[index], index }}
          >
            {props.render({ item, index })}
          </SalifyFallbackRepeaterItemContext.Provider>
        );
      })}
    </SalifyContextProvider>
  ) : null;
};
SalifyFallbackRepeater.displayName = "SalifyFallbackRepeater";

export const SalifyFallbackText = (props: SalifyFallbackCompProps) => {
  const val = useFallback({ ...props, $type: "text", path: props.path });
  return val;
};
SalifyFallbackText.displayName = "SalifyFallbackText";

export const SalifyFallbackTextTemplate = (
  props: SalifyFallbackCompProps & { params?: ObjectLiteral },
) => {
  const val = useFallback({ ...props, $type: "md", path: props.path });
  return val;
};
SalifyFallbackTextTemplate.displayName = "SalifyFallbackTextTemplate";

export const SalifyFallbackLabel = (
  props: SalifyFallbackCompProps & { render?: (value: string) => React.ReactNode },
) => {
  const val = useFallback({ ...props, _isLabel: true, $type: "text", path: props.path });
  return props.render ? props.render(val) : (val as any);
};
SalifyFallbackLabel.displayName = "SalifyFallbackLabel";

export const SalifyFallbackLabelTemplate = (
  props: SalifyFallbackCompProps & {
    params?: ObjectLiteral;
    render?: (value: string) => React.ReactNode;
  },
) => {
  const val = useFallback({ ...props, _isLabel: true, $type: "md", path: props.path });
  return props.render ? props.render(val) : (val as any);
};
SalifyFallbackLabelTemplate.displayName = "SalifyFallbackLabelTemplate";

export const SalifyFallbackValue = (
  props: SalifyFallbackCompProps & {
    params?: ObjectLiteral;
    render: (value: any) => React.ReactNode;
  },
) => {
  const val = useFallback({ $type: "text", ...props, path: props.path });
  return props.render(val);
};
SalifyFallbackValue.displayName = "SalifyFallbackValue";

function useFallback<T = any>(fallbackOptions: SalifyFallbackCompOptions): T {
  const context = React.useContext(SalifyFallbackContext);
  const val = useSelect({
    fallbackOptions,
    path: fallbackOptions.path,
    params: fallbackOptions.params,
    defaultValue: fallbackOptions.default,
    prefixPath: fallbackOptions.prefixPath,
  });
  const id = React.useId();

  useEffect(() => {
    context.simulator &&
      !Object.hasOwn(context.fallbacks, fallbackOptions.path) &&
      context.setFallbacks((state) => ({
        ...state,
        [fallbackOptions.path]: { id, ...fallbackOptions },
      }));
  });

  if (val !== EMPTY_SYMBOL) return val;
  if (!context.simulator) return null as any;

  switch (fallbackOptions.$type) {
    case "repeater":
      return [] as any;
    default:
      return "Lorem" as any;
  }
}

function selectValueFromContext(
  {
    context,
    itemContext,
    componentContext,
    params,
    prefixPath,
    defaultValue,
    fallbackOptions,
  }: {
    context: ISalifyContext;
    itemContext?: IItemContext;
    componentContext?: ISalifyComponentContext;
    params?: ObjectLiteral;
    prefixPath?: string;
    defaultValue?: string;
    fallbackOptions?: SalifyFallbackCompOptions;
  },
  path: string,
) {
  if (!path) {
    return EMPTY_SYMBOL;
  }

  let value =
    path.includes(":") || prefixPath
      ? prefixPath
        ? getter(context.data, parsePath(`${prefixPath}.${path}`), EMPTY_SYMBOL)
        : getter(context.data, parsePath(path.replace(":", ".")), EMPTY_SYMBOL)
      : ((itemContext && getter(itemContext, parsePath(`value.${path}`))) ??
        (componentContext &&
          fallbackOptions?._isLabel &&
          getter(componentContext, parsePath(`labels.${path}`))) ??
        (componentContext && getter(componentContext, parsePath(`props.${path}`))) ??
        getter(context.data, [parsePath(path), parsePath(`output.${path}`)], EMPTY_SYMBOL));

  if (value === EMPTY_SYMBOL && typeof defaultValue === "string" && defaultValue?.includes("{{")) {
    value = defaultValue;
  }

  if (typeof value === "string" && value.includes("{{")) {
    return value.replace(/{{(.+?)}}/g, (_, p1): any => {
      if (params?.hasOwnProperty(p1)) return params[p1];
      const str = selectValueFromContext({ context, params, itemContext, prefixPath }, p1);
      if (str === EMPTY_SYMBOL) return `{{${p1}}}`;
      return str;
    });
  }

  return value;
}

function parsePath(path: string) {
  if (path.endsWith(".")) return path.slice(0, -1);
  return path;
}

function useSelect<T = any>(
  options?:
    | string
    | {
        fallbackOptions?: SalifyFallbackCompOptions;
        path: string;
        params?: ObjectLiteral;
        prefixPath?: string;
        defaultValue?: T;
      },
): T {
  const state =
    typeof options === "string" || typeof options === "undefined"
      ? { path: options ?? "" }
      : options;
  const componentContext = React.useContext(SalifyComponent.Context);
  const itemContext = React.useContext(SalifyFallbackRepeaterItemContext);
  const context = React.useContext(SalifyFallbackContext);
  const val = React.useMemo(() => {
    const result = selectValueFromContext(
      {
        context,
        itemContext,
        componentContext: componentContext,
        params: state.params,
        prefixPath: state.prefixPath,
        defaultValue: state.defaultValue as any,
        fallbackOptions: state.fallbackOptions,
      },
      state.path,
    );
    if (result === EMPTY_SYMBOL) return state.defaultValue;
    return result;
  }, [
    componentContext,
    context,
    itemContext,
    state.defaultValue,
    state.fallbackOptions,
    state.params,
    state.path,
    state.prefixPath,
  ]);

  return val;
}

SalifyFallback.Context = SalifyFallbackContext;
SalifyFallback.useContext = () => React.useContext(SalifyFallbackContext);
SalifyFallback.ItemContext = SalifyFallbackRepeaterItemContext;
SalifyFallback.useItem = function <T = any>() {
  return React.useContext<ISalifyContextItem<T>>(SalifyFallbackRepeaterItemContext);
};
SalifyFallback.useSelect = useSelect;
SalifyFallback.Repeater = SalifyFallbackRepeater;
SalifyFallback.Text = SalifyFallbackText;
SalifyFallback.TextTemplate = SalifyFallbackTextTemplate;
SalifyFallback.Label = SalifyFallbackLabel;
SalifyFallback.LabelTemplate = SalifyFallbackLabelTemplate;
SalifyFallback.Value = SalifyFallbackValue;

export default SalifyFallback;
